import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import moment from 'moment';
import { PageChangedEvent } from 'ngx-bootstrap/pagination';
import { from, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { ApAlertService, ApBaseComponent, ApLockingComponent, ApLockService } from 'webcommon/core-ui';
import { FpMessagesReceivedDto, GroupLockRequestResultDto } from 'webcommon/shared';
import { FPMessageReplyModalService } from './reply/index';

@Component({
  selector: 'fp-messages-received',
  styleUrls: ['./fp-messages-received.component.less'],
  templateUrl: './fp-messages-received.component.html',
})
export class FPMessageReceivedComponent extends ApLockingComponent(ApBaseComponent) implements OnInit, OnChanges {
  @Input() baseId?: string;
  @Input() messagesReceived: FpMessagesReceivedDto[];
  @Input() loadingReceivedMessages: boolean;
  @Input() showArchived: boolean;
  @Input() readOnly: boolean;
  @Output() reload: EventEmitter<boolean> = new EventEmitter<boolean>();

  Id: string;
  currentPage = 1;
  lockType = 'Message';
  messagesReceivedPage: FpMessagesReceivedDto[] = [];
  messagesReceivedNoneArchived: FpMessagesReceivedDto[] = [];
  totalItems = 0;
  onInitComplete = false;

  constructor(
    private fpMessageReplyModalService: FPMessageReplyModalService,
    public override lockService: ApLockService,
    private alertService: ApAlertService,
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes) && this.onInitComplete) {
      if ((changes.messagesReceived) || (changes.showArchived)) {
        this.updateMessagesReceived();
      }
    }
  }

  ngOnInit() {
    this.setId();
    this.load();
    this.onInitComplete = true;
  }

  override load() {
    this.loading = false;

    super.load();
    this.DisplayOnLockErrorModal = false;

    this.updateMessagesReceived();
  }

  private updateMessagesReceived() {
    if (this.messagesReceived) {
      if (this.showArchived) {
        this.totalItems = this.messagesReceived.length;
        this.messagesReceivedNoneArchived.length = 0;
      } else {
        this.messagesReceivedNoneArchived = this.messagesReceived.filter((item) => !item.IsCompleted);
        this.totalItems = this.messagesReceivedNoneArchived.length;
      }

      // Timeout allows pagination component to reset paging button back to "page 1"
      setTimeout(() => { this.setPage(1); }, 250);
    } else {
      this.messagesReceivedNoneArchived.length = 0;
      this.messagesReceivedPage.length = 0;
      this.currentPage = 1;
      this.totalItems = 0;
    }
  }

  openReplyModal(message: FpMessagesReceivedDto) {
    this.WithLock(message.MessageId, this.lockType, () => {
      const modalPromise = this.fpMessageReplyModalService.openModal(this.Id, message);
      return from(modalPromise).pipe(
        catchError((error: unknown) => {
          return throwError(error);
        }),
      );
    }, false).pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(
      (dataSaved: boolean) => {
        this.reload.emit(dataSaved);
      },
      (error: unknown) => { this.onMessageReplyError(message, error); },
    );
  }

  private onMessageReplyError(message: FpMessagesReceivedDto, error: any) {
    const lockDto: GroupLockRequestResultDto = error?.error as GroupLockRequestResultDto;
    if (lockDto) {
      const set = new Set(lockDto.LockList.map((lock) => lock.LockedByName));
      const array = Array.from(set);
      const usernames = [...array].join(', ');
      const lockDateStart = moment.utc(lockDto.LockList[0].LockDateStart).local().format('l LT');
      const warning = `This message is currently locked by ${usernames} at ${lockDateStart} and is opened in read-only mode.`;

      const alertWarnings: string[] = [];
      alertWarnings.push(warning);

      const modalPromise = this.fpMessageReplyModalService.openModal(this.Id, message, true, alertWarnings);
      from(modalPromise).pipe(
        this.takeUntilDestroy(),
      ).subscribe(
        () => { /* success */ },
        () => { /* canceled */ },
      );
    } else {
      this.alert('An error has occurred trying to reply to a message.');
      throw error;
    }
  }

  pageChanged(pageChangedEvent: PageChangedEvent) {
    this.setPage(pageChangedEvent.page);
  }

  setPage(pageNumber: number): void {
    this.currentPage = pageNumber;

    if (this.showArchived) {
      if (this.messagesReceived) {
        this.messagesReceivedPage = this.messagesReceived.slice(10 * (pageNumber - 1), (10 * (pageNumber)));
      }
    } else {
      if (this.messagesReceivedNoneArchived) {
        this.messagesReceivedPage = this.messagesReceivedNoneArchived.slice(10 * (pageNumber - 1), (10 * (pageNumber)));
      }
    }
  }

  private setId() {
    this.Id = this.baseId + '-messages-received';
  }

  private alert(text: string) {
    this.alertService.error(text);
  }
}
